import React, { useEffect, useRef, useState } from "react";
import { NotificationProvider } from "@stonelog/stonelog-design-system";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { Collapse } from "antd";
import NeutralPersonIcon from "@assets/icons/NeutralPersonIcon";
import NeutralGroupIcon from "@assets/icons/NeutralGroupIcon";
import AdjustmentIcon from "@assets/icons/AdjustmentIcon";
import LaptopIcon from "@assets/icons/LaptopIcon";
import NavItemIcon from "@assets/icons/NavItemIcon";
import CoinIcon from "@assets/icons/CoinIcon";
import PinIcon from "@assets/icons/PinIcon";
import DeviceMachineIcon from "@assets/icons/DeviceMachineIcon";
import EyeIcon from "@assets/icons/EyeIcon";
import BottomArrow from "@assets/icons/BottomArrow";
import NotificationLayer from "@components/NotificationLayer";
import { useNotify } from "@hooks/useNotify";
import { useRoles } from "@hooks/useRoles";
import { ROLE_LIGA } from "@utils/common";
import appConfig from "../../consumingApi/services/appConfig";
import * as S from "./styles";

// role (opcional): o item só aparece para quem tem essa role
const generateRouteForLayout = (name, route, icon, role) => ({
  name,
  route,
  icon,
  role,
});

const menuLinks = [
  {
    name: "Green Angels",
    icon: <NeutralPersonIcon size={24} />,
    childrens: [
      generateRouteForLayout(
        "Gestão distr./polo",
        "/lmp/gestao-distrito-polo",
        <NeutralGroupIcon size={24} />
      ),
      generateRouteForLayout(
        "Senninha",
        "/lmp/senninha",
        <PinIcon size={24} />
      ),
    ],
  },
  {
    name: "Ferramentas",
    icon: <NeutralPersonIcon size={24} />,
    childrens: [
      generateRouteForLayout(
        "Dados do cliente",
        "/ferramentas/dados-cliente",
        <DeviceMachineIcon size={24} />
      ),
      generateRouteForLayout(
        "Remun. variável",
        "/ferramentas/remuneracao-variavel",
        <CoinIcon size={24} />
      ),
      generateRouteForLayout(
        "Casos da liga",
        "/ferramentas/casos-liga",
        <EyeIcon size={24} />
      ),
      generateRouteForLayout(
        "Anexos",
        "/ferramentas/anexos",
        <NavItemIcon size={24} />
      ),
      generateRouteForLayout(
        "Admin stone entrega",
        "/ferramentas/admin-stone-entrega",
        <GingaIcon name="headset" size={24} color="currentColor" />,
        appConfig.requirePerfilLiga ? ROLE_LIGA : undefined
      ),
      generateRouteForLayout(
        "Gestão terceira",
        "/ferramentas/operacao-terceira",
        <LaptopIcon size={24} />
      ),
    ],
  },
];

const Header = ({ icon, title }) => (
  <S.CollapseHeader className="link-collapse-header">
    {icon}
    <span>{title}</span>
  </S.CollapseHeader>
);

// O toast do design system some sozinho em 4 s (valor fixo no pacote); o item volta a ser clicável junto
const NOTIFICATION_DURATION_MS = 4000;

const MenuNavContent = () => {
  const { Panel } = Collapse;
  const roles = useRoles();
  const notify = useNotify();
  // rotas de itens com role que o usuário tentou abrir sem ter acesso
  const [deniedRoutes, setDeniedRoutes] = useState([]);
  const timersRef = useRef([]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const handleNavClick = (event, nav) => {
    if (!nav.role || roles.includes(nav.role)) return;
    event.preventDefault();
    setDeniedRoutes((prev) => (prev.includes(nav.route) ? prev : [...prev, nav.route]));
    notify.error("Seu perfil de usuário não possui acesso a essa funcionalidade");

    // quando o toast some, o item pode ser clicado de novo
    timersRef.current.push(
      setTimeout(
        () => setDeniedRoutes((prev) => prev.filter((route) => route !== nav.route)),
        NOTIFICATION_DURATION_MS
      )
    );
  };

  return (
    <S.AllPage>
      <Collapse
        defaultActiveKey={["0", "1"]}
        ghost
        expandIconPosition="end"
        expandIcon={() => <BottomArrow color={"#767F8D"} size={24} />}
      >
        {menuLinks.map(({ name, icon, childrens }, index) => (
          <Panel
            header={<Header title={name} icon={icon} />}
            key={index.toString()}
          >
            {childrens.map((nav) => (
              <S.NavLinkMenu
                to={nav.route}
                key={nav.name}
                className="ginga-nav-link-item"
                onClick={(event) => handleNavClick(event, nav)}
                $disabled={deniedRoutes.includes(nav.route)}
                aria-disabled={deniedRoutes.includes(nav.route)}
              >
                {nav.icon}
                <p>{nav.name}</p>
              </S.NavLinkMenu>
            ))}
          </Panel>
        ))}
      </Collapse>
    </S.AllPage>
  );
};

// O toast do design system precisa de um NotificationProvider acima de quem o dispara
export const MenuNav = () => (
  <NotificationProvider>
    <NotificationLayer />
    <MenuNavContent />
  </NotificationProvider>
);
